import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useData, STAGES, blockers } from '../data.js';
import { useRecords, PageHead, money, shortDate } from '../ui.js';
import { getOutbound } from '../api.js';
import type { Deal, Activity, EmailMessage, Contact, OutboundReport, Funnel } from '../api.js';

// Reporting, held to Kyle's standing rule: a view answers "what do I do", not
// "what happened". So every table here ends in something actionable, and the
// coverage section leads — a pipeline chart is useless if the top of the funnel
// is empty and nobody has noticed.

const num = (v: unknown) => (v === null || v === undefined ? 0 : Number(v) || 0);

const DEAL_P: Record<string, number> = {
  scoping: 10, discovery: 25, proposal: 50, negotiation: 70, verbal: 90, won: 100, lost: 0,
};

function Bar({ value, max, label, right }: {
  value: number; max: number; label: string; right: string;
}) {
  const pct = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return (
    <div className="barrow">
      <span className="barlabel">{label}</span>
      <span className="bartrack"><span className="barfill" style={{ width: `${pct}%` }} /></span>
      <span className="barvalue">{right}</span>
    </div>
  );
}

/**
 * One line of the funnel.
 *
 * The rate is shown BESIDE the count, never instead of it. "38%" over four
 * messages is not a rate, it is an accident, and a screen that shows only the
 * percentage invites somebody to act on one.
 */
function Step({ label, n, of, rate, note }: {
  label: string; n: number; of: number; rate: number | null; note?: string;
}) {
  const pct = of > 0 ? Math.max(2, Math.round((n / of) * 100)) : 0;
  return (
    <div className="barrow">
      <span className="barlabel">{label}</span>
      <span className="bartrack"><span className="barfill" style={{ width: `${pct}%` }} /></span>
      <span className="barvalue">
        {n}{rate === null ? '' : ` · ${rate}%`}{note ? ` ${note}` : ''}
      </span>
    </div>
  );
}

function Funnel({ f, thin }: { f: Funnel; thin: boolean }) {
  return (
    <>
      <Step label="sent" n={f.sent} of={f.sent} rate={null} />
      <Step label="opened" n={f.opened} of={f.sent} rate={f.open_rate} />
      <Step label="clicked" n={f.clicked} of={f.sent} rate={f.click_rate} />
      <Step label="replied" n={f.replied} of={f.sent} rate={f.reply_rate} />
      <Step label="bounced" n={f.bounced} of={f.sent} rate={f.bounce_rate} />
      {thin && (
        <p className="fieldhint">
          Too few messages for a rate to mean anything yet. The counts are real;
          the percentages will not settle until there are a few dozen.
        </p>
      )}
    </>
  );
}

export function Reports() {
  const { companies } = useData();
  const [outbound, setOutbound] = useState<OutboundReport | null>(null);
  const [outboundErr, setOutboundErr] = useState<string | null>(null);

  useEffect(() => {
    getOutbound(30)
      .then(setOutbound)
      // A failed aggregate must not blank the rest of the screen. Saying so is
      // better than rendering zeros that look like a real answer.
      .catch((e: unknown) => setOutboundErr(e instanceof Error ? e.message : String(e)));
  }, []);
  // limit: 500 is the server's hard ceiling. The default 200 silently froze
  // every count on this screen at 200 while presenting it as a total — a
  // report that stops counting is worse than one that says it stopped.
  const deals = useRecords<Deal & { id: string; probability: number | null }>('deals', { limit: 500 });
  const activities = useRecords<Activity & { id: string; category: string }>('activities', { limit: 500 });
  const emails = useRecords<EmailMessage & { id: string }>('emails', { limit: 500 });
  const contacts = useRecords<Contact & { id: string; company_id: string }>('contacts', { limit: 500 });

  const co = companies ?? [];
  // A failed list read now keeps rows null (see useRecords) — name the
  // failure instead of charting zeros over it.
  const listErr = deals.error ?? activities.error ?? emails.error ?? contacts.error;

  const coverage = useMemo(() => {
    const noDomain = co.filter((c) => !c.url);
    const noContacts = co.filter((c) => c.contact_count === 0);
    const noEmail = co.filter((c) => c.contact_count > 0 && c.contacts_with_email === 0);
    const noSequence = co.filter((c) => c.touches_total === 0);
    const hardBlocked = co.filter((c) => blockers(c).some((b) => b.hard));
    return { noDomain, noContacts, noEmail, noSequence, hardBlocked };
  }, [co]);

  const byStage = useMemo(
    () => STAGES.map((s) => ({ ...s, rows: co.filter((c) => c.stage === s.key) })),
    [co],
  );
  const maxStage = Math.max(1, ...byStage.map((s) => s.rows.length));

  const dealRows = deals.rows ?? [];
  const openDeals = dealRows.filter((d) => d.stage !== 'won' && d.stage !== 'lost');
  const wonDeals = dealRows.filter((d) => d.stage === 'won');
  const lostDeals = dealRows.filter((d) => d.stage === 'lost');
  const weighted = openDeals.reduce(
    (s, d) => s + num(d.amount) * ((d.probability ?? DEAL_P[d.stage] ?? 0) / 100), 0);
  const winRate = wonDeals.length + lostDeals.length > 0
    ? Math.round((wonDeals.length / (wonDeals.length + lostDeals.length)) * 100) : null;

  const acts = activities.rows ?? [];
  const salesActs = acts.filter((a) => a.category === 'sales');
  const mktActs = acts.filter((a) => a.category === 'marketing');
  const sent = (emails.rows ?? []).filter((m) => m.status === 'sent');
  const queued = (emails.rows ?? []).filter((m) => m.status === 'scheduled');
  const drafts = (emails.rows ?? []).filter((m) => m.status === 'draft');

  const gaps: { label: string; n: number; to: string; why: string }[] = [
    { label: 'accounts with no website', n: coverage.noDomain.length, to: '/companies',
      why: 'Cannot be enriched or searched — the whole research loop is blocked on a URL.' },
    { label: 'accounts with nobody on them', n: coverage.noContacts.length, to: '/research',
      why: 'A name, not a target. No committee means no outreach is possible.' },
    { label: 'accounts where nobody has an address', n: coverage.noEmail.length, to: '/contacts',
      why: 'Contacts exist but none are reachable. Reveal costs one credit each.' },
    { label: 'accounts with no sequence planned', n: coverage.noSequence.length, to: '/companies',
      why: 'Nothing will happen on these until touches are planned.' },
    { label: 'accounts hard-blocked', n: coverage.hardBlocked.length, to: '/',
      why: 'Something is stopping these entirely. They will not move on their own.' },
  ].filter((g) => g.n > 0).sort((a, b) => b.n - a.n);

  return (
    <>
      <PageHead
        title="Reports"
        sub="Where the funnel is leaking, and what it is worth if it does not."
      />
      {listErr && (
        <p className="warnbox">Some numbers below may be missing: {listErr}</p>
      )}

      <section className="panel">
        <h4>What is blocking the funnel</h4>
        {gaps.length === 0 && <p className="meta">Nothing structural. Every account can move.</p>}
        {gaps.map((g) => (
          <Link className="row" to={g.to} key={g.label}>
            <span className="rname"><strong className="bignum">{g.n}</strong> {g.label}</span>
            <span className="meta">{g.why}</span>
          </Link>
        ))}
      </section>

      <div className="stats">
        <div className="stat"><span>Accounts</span><strong>{co.length}</strong></div>
        <div className="stat"><span>Contacts</span><strong>{contacts.rows?.length ?? 0}</strong></div>
        <div className="stat"><span>Open pipeline</span><strong>
          {money(openDeals.reduce((s, d) => s + num(d.amount), 0))}</strong></div>
        <div className="stat"><span>Weighted</span><strong>{money(weighted)}</strong></div>
      </div>

      <section className="panel">
        <h4>Accounts by prospecting stage</h4>
        {byStage.map((s) => (
          <Bar key={s.key} label={s.label} value={s.rows.length} max={maxStage}
            right={String(s.rows.length)} />
        ))}
      </section>

      <section className="panel">
        <h4>Deal pipeline</h4>
        {dealRows.length === 0 ? (
          <p className="meta">
            No deals yet, so there is no pipeline to report. Everything above is top-of-funnel.
          </p>
        ) : (
          <>
            {Object.keys(DEAL_P).map((stage) => {
              const inStage = dealRows.filter((d) => d.stage === stage);
              if (!inStage.length) return null;
              const v = inStage.reduce((s, d) => s + num(d.amount), 0);
              return <Bar key={stage} label={stage} value={v}
                max={Math.max(1, ...Object.keys(DEAL_P).map((k) =>
                  dealRows.filter((d) => d.stage === k).reduce((s, d) => s + num(d.amount), 0)))}
                right={`${money(v)} · ${inStage.length}`} />;
            })}
            <div className="row">
              <span className="rname">Win rate</span>
              <span className="meta">
                {winRate === null
                  ? 'Nothing closed yet — needs at least one won or lost deal.'
                  : `${winRate}% across ${wonDeals.length + lostDeals.length} closed deals`}
              </span>
            </div>
            {openDeals.filter((d) => d.expected_close &&
              new Date(d.expected_close) < new Date()).map((d) => (
              <div className="row" key={d.id}>
                <span className="rname">{d.name}</span>
                <span className="flag flag-warn">
                  close date passed {shortDate(d.expected_close)}
                </span>
              </div>
            ))}
          </>
        )}
      </section>

      <section className="panel">
        <h4>Activity</h4>
        <div className="row">
          <span className="rname">Sales activity logged</span>
          <span className="meta">{salesActs.length} events</span>
        </div>
        <div className="row">
          <span className="rname">Marketing activity logged</span>
          <span className="meta">
            {mktActs.length} events
            {mktActs.length === 0 && ' — opens and replies land here once sending exists'}
          </span>
        </div>
        <div className="row">
          <span className="rname">Email queue</span>
          <span className="meta">
            {sent.length} sent · {queued.length} queued · {drafts.length} drafts
            {sent.length >= 200 && ' (queue view only — the funnel below is the real count)'}
          </span>
        </div>
      </section>

      {/* THE NUMBER AN SDR MOTION IS ACTUALLY JUDGED ON.
          Counted by the database over the whole window rather than from the
          rows this page happens to hold — /api/records stops at 200, so a
          browser-side funnel would be right for a fortnight and quietly wrong
          forever after. See reports-core.ts. */}
      <section className="panel">
        <h4>
          Cold outreach
          {outbound && (
            <span className="count">
              {outbound.cold.sent === 0
                ? 'nothing sent yet'
                : `${outbound.cold.replied} replies from ${outbound.cold.sent} sent`
                  + `${outbound.cold.reply_rate === null ? '' : ` · ${outbound.cold.reply_rate}%`}`}
            </span>
          )}
        </h4>

        {outboundErr && <p className="err">Could not count the funnel: {outboundErr}</p>}
        {!outbound && !outboundErr && <p className="meta">Counting…</p>}

        {outbound && outbound.cold.sent === 0 && (
          <p className="meta">
            No cold message has been sent from this workspace. Arm a sequence and
            this fills in — last {outbound.days} days.
          </p>
        )}

        {outbound && outbound.cold.sent > 0 && (
          <>
            <Funnel f={outbound.cold} thin={outbound.cold.sent < 30} />
            <p className="fieldhint">{outbound.open_rate_caveat}</p>
          </>
        )}

        {outbound && outbound.warm.sent > 0 && (
          <>
            <div className="row">
              <span className="rname">One-to-one, for comparison</span>
              <span className="meta">
                {outbound.warm.replied} of {outbound.warm.sent} replied
                {outbound.warm.reply_rate === null ? '' : ` · ${outbound.warm.reply_rate}%`}
                {' '}— counted separately on purpose: folding it in would make cold
                outreach look like it is working when it is not.
              </span>
            </div>
          </>
        )}

        {outbound && (
          <div className="row">
            <span className="rname">Cannot be mailed again</span>
            <span className="meta">
              {outbound.suppressed.suppressed} of {outbound.suppressed.contacts} contacts
              {outbound.suppressed.suppressed > 0
                && ` — ${outbound.suppressed.unsubscribed} opted out, `
                   + `${outbound.suppressed.bounced} bounced`}
            </span>
          </div>
        )}
      </section>
    </>
  );
}
